import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Globals } from 'app/_globals';
import { SiteSummary } from 'app/_models/site-summary.model';
import { WorkflowItem } from 'app/_models/workflow-items/workflow-item.model';
import { FilterSummaryItem } from 'app/_utils/filter-summary.utils';
import { buildWorkflowBackParams } from 'app/_utils/workflow-back-link.utils';
import { NotificationService } from 'app/_services/notification.service';
import { SiteService } from 'app/_services/site.service';
import { WorkflowItemFilters, WorkflowItemService } from 'app/_services/workflow-items.service';
import { WorkflowAccessService } from 'app/_services/workflow-access.service';
import { forkJoin, timer, Subscription } from 'rxjs';
import { environment } from 'environments/environment';
import { LoggerService } from 'app/_services/logger.service';
import { finalize } from 'rxjs/operators';

interface WorkflowTab {
  key: string;
  label: string;
  category: string;
  enabled: boolean;
  count: number;
}

const WORKER_COMPETENCY_TAB_KEY = 'WORKER_COMPETENCIES';

@Component({
  selector: 'inbox',
  templateUrl: './inbox.component.html',
})
export class InboxComponent implements OnInit, OnDestroy {
  public workflowTabs: WorkflowTab[] = [];

  public activeWorkflowTabKey = '';

  private requestedTabKey = '';
  routeRole: string;
  fetchDataObservable: any;

  pageSizes: any = environment.perPageOptionsSmall;
  completePagination: any;
  pendingPagination: any;
  @ViewChild('pendingPaginator') pendingPaginator;
  @ViewChild('completePaginator') completePaginator;

  /**
   * How far back the approvals filters may reach. Both APIs enforce the same window as a hard
   * floor and they allow a day more than this so that
   * sending local midnight for the earliest selectable day is never clamped. Keep in step with
   * skylark.approvals.filter-window-days in skylark-backend and skylark-competencies.
   */
  public static readonly FILTER_WINDOW_DAYS = 28;

  /**
   * How the window is worded to users. Spelled out rather than derived from the constant above,
   * Keep the two in step.
   */
  public static readonly FILTER_WINDOW_LABEL = '4 weeks';

  public readonly filterWindowNote =
    `Date filters cover the last ${InboxComponent.FILTER_WINDOW_LABEL}. Approvals modified before then are not listed.`;

  public sitesArray: { label: string, value: number }[] = [{ label: 'All Sites', value: undefined }];
  private sitesLoaded = false;
  private sitesLoading = false;

  /** Holds the first fetch while a siteId from the URL is resolved against the site list. */
  private awaitingSiteResolution = false;

  public selectedSite: any = { label: 'All Sites', value: undefined };
  public fromDate: Date = null;
  public toDate: Date = null;

  /** Calendar bounds, kept in step with the API's floor by refreshFilterBounds(). */
  public earliestFilterDate: Date = null;
  public today: Date = null;
  public keyword = '';
  public activeFiltersSummary: FilterSummaryItem[] = [];

  // Both tables hit the same URL, so the shared latest-only interceptor cannot tell an outdated
  // keyword request from the sibling list's request - it keys on the path and would have them
  // cancel each other. Holding one subscription per table and dropping the previous does the same
  // job with the right granularity: an in-flight fetch is aborted only by its own replacement.
  private pendingItemsSubscription: Subscription;
  private completeItemsSubscription: Subscription;

  private lastFailureNotifiedAt: { [key: string]: number } = {};
  private readonly failureNotifyWindowMs = 5000;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private workflowItemService: WorkflowItemService,
    private workflowAccessService: WorkflowAccessService,
    private notificationService: NotificationService,
    private loggerService: LoggerService,
    private siteService: SiteService,
    private globals: Globals,
  ) {
    this.routeRole = globals.user.routeRole;
    this.workflowTabs = this.buildWorkflowTabs();
    this.activeWorkflowTabKey = this.workflowTabs[0] ? this.workflowTabs[0].key : '';
  }

  public pendingItems: { date: Date; task: string; data: WorkflowItem; }[];
  public completedItems: { date: Date; task: string; data: WorkflowItem; }[];
  public pendingCols: ({ field: string; header: string; type?: string; linkText?: string; displayField?: string })[];
  public completedCols: ({ field: string; header: string; type?: string; displayField?: string })[];
  public totalWorkflowItemsCount = 0;
  public pendingLoading = false;
  public completeLoading = false;

  ngOnInit() {
    this.refreshFilterBounds();

    const queryParams = this.activatedRoute.snapshot.queryParams;
    this.requestedTabKey = queryParams.tab;
    const requestedTab = this.workflowTabs.find(tab => tab.key === this.requestedTabKey && tab.enabled);
    const firstEnabledTab = this.workflowTabs.find(tab => tab.enabled);

    if (requestedTab) {
      this.activeWorkflowTabKey = requestedTab.key;
    } else if (firstEnabledTab) {
      this.activeWorkflowTabKey = firstEnabledTab.key;
    }

    this.readFiltersFromQueryParams(queryParams);
    this.updateActiveFiltersSummary();

    if (queryParams.siteId) {
      // The site list has to arrive before the first fetch can filter by it, so hold the fetch
      // rather than firing one unfiltered and immediately replacing it - that second round is not
      // free, and refreshTabCounts() keeps no subscription to cancel.
      this.awaitingSiteResolution = true;
      this.ensureSitesLoaded();
    }

    this.resetPagination();

    // refresh every 60 seconds
    this.fetchDataObservable = timer(1000 * 60, 1000 * 60).subscribe(() => {
      this.refreshFilterBounds();
      this.refreshActiveTab();
    });

    this.pendingCols = [
      { field: 'date', header: 'Updated On', type: 'date' },
      { field: 'requestedBy', header: 'Requested By', type: 'username', displayField: 'requestedByName' },
      { field: 'task', header: 'Task' },
      { field: 'details', header: 'Details'},
      { field: 'actions', header: 'Actions', type: 'action_view' },
    ];

    this.completedCols = [
      { field: 'date', header: 'Updated On', type: 'date' },
      { field: 'requestedBy', header: 'Requested By', type: 'username', displayField: 'requestedByName' },
      { field: 'task', header: 'Task' },
      { field: 'details', header: 'Details'},
      { field: 'status', header: 'Status', type: 'workflow_status' },
      { field: 'rejectionReason', header: 'Reason', type: 'truncated_text' },
      { field: 'actions', header: 'Actions', type: 'action_view' },
    ];

  }

  ngOnDestroy() {
    if (this.fetchDataObservable) {
      this.fetchDataObservable.unsubscribe();
    }

    this.cancelInFlight(this.pendingItemsSubscription);
    this.cancelInFlight(this.completeItemsSubscription);
  }

  private getPendingItems() {
    const activeTab = this.getActiveTab();
    if (!activeTab || !activeTab.enabled) {
      this.pendingItems = [];
      this.pendingPagination.totalRecords = 0;
      this.pendingLoading = false;
      return;
    }

    // Cancel before raising the flags: unsubscribing runs the superseded request's finalize, which
    // would otherwise switch the spinner straight back off.
    this.cancelInFlight(this.pendingItemsSubscription);
    this.pendingLoading = true;

    // The badge count deliberately stays unfiltered - it answers "how much is waiting for you", so
    // it must not move as filters are typed. The filtered total comes from X-Total-Count instead,
    // which is why it is preferred below whenever any filter is active.
    this.pendingItemsSubscription = forkJoin([
      this.workflowItemService.getWorkflowItemsByCategory(
        activeTab.category, 'PENDING', this.pendingPagination, this.buildFilters()),
      this.workflowItemService.getPendingWorkflowCount(activeTab.category),
    ]).pipe(finalize(() => {
      this.pendingLoading = false;
    })).subscribe(([data, totalRecords]) => {
      this.pendingItems = data.body.map(result => ({
        date: result.lastModifiedDate,
        requestedBy: result.createdBy,
        requestedByName: result.requestedByName,
        task: result.getTaskDetails(),
        data: result,
        details: result.details,
      }));
      this.pendingPagination.totalRecords = this.hasActiveFilters()
        ? data.totalRecords
        : (totalRecords || data.totalRecords);
    }, error => {
      console.error(error);

      this.loggerService.logToServer(
        this.globals.user.id,
        `Inbox page, pending items failed - ${JSON.stringify(this.pendingPagination)} - ${JSON.stringify(error)};`
      );
      this.notifyLoadFailure('inbox-load', 'Couldn\'t load pending approvals', 'Please try again later');
    });
  }

  private refreshTabCounts() {
    const enabledCategories = this.workflowTabs.filter((tab) => tab.enabled).map((tab) => tab.category);

    this.workflowItemService.getPendingWorkflowCounts(enabledCategories).subscribe((counts) => {
      this.totalWorkflowItemsCount = counts.totalRecords || 0;
      this.workflowTabs.forEach((tab) => {
        tab.count = counts.byCategory[tab.category] || 0;
      });
    }, (error) => {
      console.error(error);

      forkJoin(this.workflowTabs.map((tab) =>
        this.workflowItemService.getPendingWorkflowItemsCountByCategory(tab.category)
      )).subscribe((fallbackCounts: number[]) => {
        this.totalWorkflowItemsCount = fallbackCounts.reduce((acc, count) => acc + (count || 0), 0);
        this.workflowTabs.forEach((tab, index) => {
          tab.count = fallbackCounts[index] || 0;
        });
      }, (fallbackError) => {
        // getPendingWorkflowItemsCountByCategory currently swallows failures into 0, so this is a
        // safety net rather than a live path - without it a counts outage would silently show
        // every badge as 0.
        console.error(fallbackError);
        this.notifyLoadFailure('inbox-load', 'Couldn\'t load approval counts', 'The tab badges may be out of date');
      });
    });
  }

  private getCompleteItems() {
    const activeTab = this.getActiveTab();
    if (!activeTab || !activeTab.enabled) {
      this.completedItems = [];
      this.completePagination.totalRecords = 0;
      this.completeLoading = false;
      return;
    }

    this.cancelInFlight(this.completeItemsSubscription);
    this.completeLoading = true;

    this.completeItemsSubscription = this.workflowItemService
      .getWorkflowItemsByCategory(activeTab.category, 'APPROVED,REJECTED', this.completePagination, this.buildFilters())
      .pipe(finalize(() => {
        this.completeLoading = false;
      }))
      .subscribe(data => {
      this.completedItems = data.body.map((workFlow) => {
        return {
          date: workFlow.lastModifiedDate,
          requestedBy: workFlow.createdBy,
          requestedByName: workFlow.requestedByName,
          task: workFlow.getTaskDetails(),
          data: workFlow,
          details: workFlow.details,
          status: workFlow.status,
          rejectionReason: workFlow.rejectionReason,
        };
      });
      this.completePagination.totalRecords = data.totalRecords;
    }, error => {
      console.error(error);

      this.loggerService.logToServer(
        this.globals.user.id,
        `Inbox page, complete items failed - ${JSON.stringify(this.completePagination)} - ${JSON.stringify(error)};`
      );
      this.notifyLoadFailure('inbox-load', 'Couldn\'t load recent approval changes', 'Please try again later');
    });
  }

  public setActiveWorkflowTab(tabKey: string) {
    if (!this.isTabEnabled(tabKey) || this.activeWorkflowTabKey === tabKey) {
      return;
    }

    this.activeWorkflowTabKey = tabKey;

    // Every filter carries across tabs as the user is usually chasing the same thing,
    // and "show me everything for site X" is the natural reading. What the site filter *means*
    // changes per tab, which is what siteFilterLabel and siteFilterHint are for.
    this.updateActiveFiltersSummary();
    this.writeFiltersToQueryParams();
    this.goToFirstPage();
    this.refreshActiveTab();
  }

  // ---------------------------------------------------------------- filters

  /**
   * The picker is shown on every tab, but it reaches the items two different ways depending on the
   * tab, so it is labelled and explained per tab rather than left to be guessed at.
   */
  public isSiteFilterAvailable(): boolean {
    return true;
  }

  public get siteFilterLabel(): string {
    switch (this.activeWorkflowTabKey) {
      case 'CONTRACTOR_CERTIFICATES':
        return 'Site (contractor linked to)';
      case WORKER_COMPETENCY_TAB_KEY:
        return 'Site (worker assigned)';
      case 'RESTRICTIONS':
      case 'ASSET_SCHEDULES':
      default:
        return 'Site';
    }
  }

  /**
   * Both competency tabs warn that a request can appear under more than one site, because neither
   * request carries a site of its own: each is matched through the people it covers, and a worker
   * or a contractor can be at several of one operator's sites at once. The other two tabs need no
   * such warning - a restriction or a schedule is raised against exactly one site.
   */
  public get siteFilterHint(): string {
    switch (this.activeWorkflowTabKey) {
      case 'CONTRACTOR_CERTIFICATES':
        return 'Shows certificate requests for contractors linked to this site. '
          + 'The same certificate request may appear under more than one site.';
      case WORKER_COMPETENCY_TAB_KEY:
        return 'Shows competency requests for workers assigned to this site. '
          + 'The same competency request may appear under more than one site.';
      case 'ASSET_SCHEDULES':
        return 'Shows asset schedule requests for this site.';
      case 'RESTRICTIONS':
      default:
        return 'Shows restriction requests for this site.';
    }
  }

  public onSelectedSiteChange(selected: any) {
    this.selectedSite = selected;
    this.applyFilterChange();
  }

  public onKeywordChanged(keyword: string) {
    const next = keyword || '';

    if (next === this.keyword) {
      return;
    }

    this.keyword = next;
    this.applyFilterChange();
  }

  public onFromDateChange(value: Date) {
    this.fromDate = value;
    this.applyFilterChange();
  }

  public onToDateChange(value: Date) {
    this.toDate = value;
    this.applyFilterChange();
  }


  public clearSite() {
    if (!this.selectedSite || this.selectedSite.value == null) {
      return;
    }

    this.selectedSite = this.sitesArray[0];
    this.applyFilterChange();
  }

  public clearFromDate() {
    if (!this.fromDate) {
      return;
    }

    this.fromDate = null;
    this.applyFilterChange();
  }

  public clearToDate() {
    if (!this.toDate) {
      return;
    }

    this.toDate = null;
    this.applyFilterChange();
  }

  /** Called from the template, so it reads the fields directly rather than rebuilding the filters. */
  public hasActiveFilters(): boolean {
    const siteChosen = this.isSiteFilterAvailable() && !!this.selectedSite && this.selectedSite.value != null;
    return siteChosen || !!this.keyword || !!this.fromDate || !!this.toDate;
  }

  private applyFilterChange() {
    this.updateActiveFiltersSummary();
    this.writeFiltersToQueryParams();
    // Without this a narrowed result leaves the user stranded on a page that no longer exists.
    this.goToFirstPage();
    this.refreshActiveTab();
  }

  private buildFilters(): WorkflowItemFilters {
    return {
      siteId: this.isSiteFilterAvailable() && this.selectedSite ? this.selectedSite.value : undefined,
      textFilter: this.keyword,
      // toISOString() is required, not just convenient: the main API runs on Java 8, where an
      // instant carrying an offset rather than Z fails to parse. The end of the To day is included
      // so picking the same date for both bounds does not exclude everything on it.
      fromDate: this.fromDate ? this.startOfDay(this.fromDate).toISOString() : undefined,
      toDate: this.toDate ? this.endOfDay(this.toDate).toISOString() : undefined,
    };
  }

  /**
   * Recomputes the calendar bounds. Called at init and on each poll tick rather than exposed as
   * getters: the template binds these straight into p-calendar, and handing it a fresh Date on
   * every change-detection pass rebuilds its month view. Refreshing them on the tick is what keeps
   * a tab left open overnight from offering a date the API would then clamp.
   */
  private refreshFilterBounds() {
    const earliest = new Date();
    earliest.setDate(earliest.getDate() - InboxComponent.FILTER_WINDOW_DAYS);

    this.earliestFilterDate = this.startOfDay(earliest);
    this.today = this.endOfDay(new Date());
  }

  /**
   * A shared or bookmarked filter URL can carry a date from before the window. The API clamps it
   * silently, so clamp it here too - otherwise the calendar and the filter summary would claim a
   * range the results do not actually cover.
   */
  private clampToWindow(value: Date): Date {
    const earliest = this.earliestFilterDate;
    return value < earliest ? earliest : value;
  }

  private startOfDay(value: Date): Date {
    const start = new Date(value.getTime());
    start.setHours(0, 0, 0, 0);
    return start;
  }

  private endOfDay(value: Date): Date {
    const end = new Date(value.getTime());
    end.setHours(23, 59, 59, 999);
    return end;
  }

  private updateActiveFiltersSummary() {
    const summary: FilterSummaryItem[] = [];

    if (this.isSiteFilterAvailable() && this.selectedSite && this.selectedSite.value != null) {
      summary.push({ label: 'Site is', value: this.selectedSite.label });
    }

    if (this.keyword) {
      summary.push({ label: 'Matching', value: this.keyword });
    }

    if (this.fromDate) {
      summary.push({ label: 'Updated from', value: this.fromDate.toLocaleDateString('en-GB') });
    }

    if (this.toDate) {
      summary.push({ label: 'Updated to', value: this.toDate.toLocaleDateString('en-GB') });
    }

    this.activeFiltersSummary = summary;
  }

  /**
   * The one description of what the filters look like in a URL. Both the inbox's own URL and the
   * params handed to an approval page are built from this, so the filtered view the user returns to
   * cannot drift from the one they left the next time a filter is added.
   *
   * Nulls are deliberate: they are what removes a stale param under queryParamsHandling: 'merge'.
   */
  private buildFilterQueryParams(): any {
    return {
      tab: this.activeWorkflowTabKey,
      siteId: this.isSiteFilterAvailable() && this.selectedSite && this.selectedSite.value != null
        ? this.selectedSite.value : null,
      keyword: this.keyword || null,
      from: this.fromDate ? this.startOfDay(this.fromDate).toISOString() : null,
      to: this.toDate ? this.startOfDay(this.toDate).toISOString() : null,
    };
  }

  /**
   * Filters live in the URL so a filtered view survives the click-through to an approval page and
   * back, and can be shared. Uses the same merge-and-replace navigation as the tab key.
   */
  private writeFiltersToQueryParams() {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: this.buildFilterQueryParams(),
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private readFiltersFromQueryParams(queryParams: any) {
    this.keyword = queryParams.keyword || '';
    this.fromDate = this.parseQueryDate(queryParams.from);
    this.toDate = this.parseQueryDate(queryParams.to);
    // selectedSite is resolved once the site list arrives - see loadSitesForFilter.
  }

  private parseQueryDate(value: string): Date {
    if (!value) {
      return null;
    }

    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : this.clampToWindow(parsed);
  }

  public ensureSitesLoaded() {
    if (this.sitesLoaded || this.sitesLoading) {
      return;
    }

    this.sitesLoading = true;

    this.siteService.getSiteListSummary().pipe(finalize(() => {
      this.sitesLoading = false;
    })).subscribe((sites: SiteSummary[]) => {
      this.sitesLoaded = true;
      this.sitesArray = [{ label: 'All Sites', value: undefined }].concat(
        sites.map((site) => ({ label: site.siteName, value: site.siteId }))
      );

      const requestedSiteId = Number(this.activatedRoute.snapshot.queryParams.siteId);
      const requestedSite = this.sitesArray.find((site) => site.value === requestedSiteId);
      this.selectedSite = requestedSite || this.sitesArray[0];
      this.updateActiveFiltersSummary();

      this.releaseSiteResolution();
    }, (error) => {
      console.error(error);
      this.sitesArray = [{ label: 'All Sites', value: undefined }];
      this.selectedSite = this.sitesArray[0];

      this.notifyLoadFailure(
        'site-list',
        'Couldn\'t load the site list',
        'Filtering by site is unavailable. Open the dropdown to try again.'
      );

      // Still release the hold, or a failed site lookup would leave both tables permanently empty.
      this.releaseSiteResolution();
    });
  }

  private releaseSiteResolution() {
    if (!this.awaitingSiteResolution) {
      return;
    }

    this.awaitingSiteResolution = false;
    this.refreshActiveTab();
  }

  /**
   * The inbox fires several requests together, so one outage would otherwise stack a toast per
   * request. Callers that fail as a group share a key: the first failure's message is shown and
   * the rest are suppressed for a few seconds.
   */
  private notifyLoadFailure(key: string, title: string, message: string) {
    const lastAt = this.lastFailureNotifiedAt[key];

    if (lastAt && (Date.now() - lastAt) < this.failureNotifyWindowMs) {
      return;
    }

    this.lastFailureNotifiedAt[key] = Date.now();
    this.notificationService.notify('danger', title, message);
  }

  private cancelInFlight(subscription: Subscription) {
    if (subscription && !subscription.closed) {
      subscription.unsubscribe();
    }
  }

  public goToTask(event) {
    switch (event.data.workflowType) {
      case 'ASSET_RESTRICTION':
        this.router.navigate(
          [
            '/' + this.routeRole,
            'sites',
            event.data.site.id,
            'assets',
            event.data.asset.id,
            'restriction-approval',
            event.data.id,
          ],
          { queryParams: this.getWorkflowApprovalBackParams() }
        );
        break;
      case 'SITE_RESTRICTION':
        this.router.navigate(
          ['/' + this.routeRole, 'sites', event.data.site.id, 'restriction-approval', event.data.id],
          { queryParams: this.getWorkflowApprovalBackParams() }
        );
        break;
      case 'CONTRACTOR_COMPETENCY':
        this.workflowItemService.cacheWorkflowItem(event.data, event.data.workflowType);
        this.router.navigate(['/' + this.routeRole, 'certificate-approval', 'contractor', event.data.id], {
          queryParams: this.getWorkflowApprovalBackParams(),
        });
        break;
      case 'WORKER_COMPETENCY':
        // Worker competency approvals use a dedicated API and a slightly different approval page flow.
        // Caching here avoids reloading the same payload after the inbox click-through.
        this.workflowItemService.cacheWorkflowItem(event.data, event.data.workflowType);
        this.router.navigate(['/' + this.routeRole, 'certificate-approval', 'worker', event.data.id], {
          queryParams: this.getWorkflowApprovalBackParams(),
        });
        break;
      case 'ASSET_BOOKING_REQUEST':
        this.router.navigate(['/' + this.routeRole + '/sites/' + event.data.site.id + '/schedule/'], { queryParams: { workflowId: event.data.id, date: event.data.getStartDateWithoutTime() }});
        break;
      case 'ASSET_BOOKING_CANCELLATION':
        this.router.navigate(['/' + this.routeRole + '/sites/' + event.data.site.id + '/schedule/'], { queryParams: { workflowId: event.data.id, date: event.data.getStartDateWithoutTime() }});
        break;
      default:
        break;
    }
  }

  public completePaginate(event) {
    this.completePagination.size = event.rows;
    this.completePagination.page = event.page;
    this.getCompleteItems();
  }


  public pendingPaginate(event) {
    this.pendingPagination.size = event.rows;
    this.pendingPagination.page = event.page;
    this.getPendingItems();
  }

  public resetPagination() {
    this.completePagination = {
      size: this.pageSizes[0],
      page: 0,
      totalRecords: 0,
    };

    this.pendingPagination = {
      size: this.pageSizes[0],
      page: 0,
      totalRecords: 0,
    };

    this.goToFirstPage();

    this.refreshActiveTab();
  }

  public get totalPendingItemsCount(): number {
    return this.totalWorkflowItemsCount;
  }

  public get totalApprovalsCount(): number {
    return this.totalWorkflowItemsCount;
  }

  public isTabEnabled(tabKey: string): boolean {
    const tab = this.workflowTabs.find(item => item.key === tabKey);
    return !!tab && tab.enabled;
  }

  public isActiveTabAvailable(): boolean {
    const activeTab = this.getActiveTab();
    return !!activeTab && activeTab.enabled;
  }

  private getActiveTab() {
    return this.workflowTabs.find(tab => tab.key === this.activeWorkflowTabKey);
  }

  private buildWorkflowTabs(): WorkflowTab[] {
    const tabs: WorkflowTab[] = [];
    const visibleCategories = new Set(this.workflowAccessService.getVisibleWorkflowCategories());

    if (visibleCategories.has('worker-competency')) {
      tabs.push({ key: 'WORKER_COMPETENCIES', label: 'Worker Competencies', category: 'worker-competency', enabled: true, count: 0 });
    }

    if (visibleCategories.has('contractor-cert')) {
      tabs.push({ key: 'CONTRACTOR_CERTIFICATES', label: 'Contractor Certificates', category: 'contractor-cert', enabled: true, count: 0 });
    }

    if (visibleCategories.has('restriction')) {
      tabs.push({ key: 'RESTRICTIONS', label: 'Restrictions', category: 'restriction', enabled: true, count: 0 });
    }

    if (visibleCategories.has('asset-booking')) {
      tabs.push({ key: 'ASSET_SCHEDULES', label: 'Asset Schedules', category: 'asset-booking', enabled: true, count: 0 });
    }

    return tabs;
  }

  private getWorkflowApprovalBackParams(params: any = {}) {
    return Object.assign({}, params, buildWorkflowBackParams(this.buildFilterQueryParams()));
  }

  private refreshActiveTab() {
    if (this.awaitingSiteResolution) {
      return;
    }

    this.refreshTabCounts();

    if (!this.isActiveTabAvailable()) {
      this.pendingItems = [];
      this.completedItems = [];
      this.pendingPagination.totalRecords = 0;
      this.completePagination.totalRecords = 0;
      return;
    }

    this.getPendingItems();
    this.getCompleteItems();
  }

  private getTotalWorkflowItemsCount(): number {
    return this.workflowTabs
      .filter((tab) => tab.enabled)
      .map((tab) => tab.count || 0)
      .reduce((acc, count) => acc + count, 0);
  }

  private goToFirstPage() {
    this.completePagination.page = 0;     // Backend: reset page index to fetch from API
    this.pendingPagination.page = 0;      // Backend: reset page index to fetch from API

    if (this.completePaginator) {
      this.completePaginator.first = 0;   // Frontend: update visual page indicator (PrimeNg)
    }

    if (this.pendingPaginator) {
      this.pendingPaginator.first = 0;    // Frontend: update visual page indicator (PrimeNg)
    }
  }
}
